import { For, Show, createMemo, createSignal, onCleanup, onMount } from "solid-js"
import { useReportBrowser } from "./report-browser-context"
import { ReportGroup, ReportInfoCard } from "st-app/api"
import InlineEdit from "../inline-edit/inline-edit"
import Button from "../st-button/st-button"
import STModal from "../st-modal/st-modal"
import { reportBrowser as api } from 'st-app/api'
import SideNav from "./side-nav"
import ReportEditMenu from "./report-edit-menu"

export const TileView = () => {
    const reportBrowser = useReportBrowser()!
    let reportGrid: HTMLDivElement;

    function onKeydown(e: KeyboardEvent) {
        switch (e.key) {
            case 'Escape':
                reportBrowser.setSelectedGroup(null)
                break;
        }
    }

    onMount(() => {
        document.addEventListener('keydown', onKeydown)

        setTimeout(() => {
            reportGrid?.scrollTo({ top: reportBrowser.state.reportBrowserScrollTop, behavior: 'auto' })
        })
    })
    
    onCleanup(() => {
        document.removeEventListener('keydown', onKeydown)
    })

    const onScroll = (e: Event) => {
        const el = e.currentTarget as HTMLDivElement
        if (el) {
            reportBrowser.setReportBrowserScrollTop(el.scrollTop)
        }
    }

    return (
        <div style="display: flex; justify-content: center; overflow-y: auto; padding-block: 8px;">
            <Show when={!reportBrowser.shouldShowReports()}>
                <div class="report-browser-grid" style={`grid-template-columns: repeat(auto-fill, minmax(224px, 1fr));`}>
                    <For each={reportBrowser.state.groups}>{(group) => (
                        <GroupTile group={group}></GroupTile>
                    )}</For>
                </div>
            </Show>

            <Show when={reportBrowser.shouldShowReports()}>
                <div style="display: flex; flex-grow: 1;">
                    <SideNav></SideNav>
                    <div class="report-browser-grid" ref={reportGrid!} onScroll={onScroll} style={`grid-template-columns: repeat(auto-fill, minmax(324px, 1fr)); overflow-y: auto; justify-items: center; flex-grow: 1;`}>
                        <Show when={reportBrowser.reports().length === 0}>
                            <div style="width: 100%; height: 80vh; display: flex; justify-content: center; align-items: center; grid-column: 1/-1; font-size: 2rem;">
                                No Reports Found.
                            </div>
                        </Show>
                        <Show when={reportBrowser.reports().length > 0}>
                            <For each={reportBrowser.reports()}>{(report) => (
                                <ReportTile report={report}></ReportTile>
                            )}</For>
                        </Show>
                    </div>
                </div>
            </Show>

        </div>
    )
}



interface GroupProps {
    group: ReportGroup
}

interface GroupUpdate {
    description?: string
    thumbnail?: string
    newThumbnailFile?: File
}

const GroupTile = (props: GroupProps) => {
    const reportBrowser = useReportBrowser()!
    const isChangingThumbnailSignal = createSignal(false)
    const [_isChangingThumbnail, setIsChangingThumbnail] = isChangingThumbnailSignal
    const [newThumbnailFile, setNewThumbnailFile] = createSignal<File | undefined>()

    const onClickChangeThumbnail = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        setIsChangingThumbnail(true)
    }

    const groupTitle = createSignal(props.group.description ?? '')

    const save = async (update: GroupUpdate) => {
        let thumbnail = null;
        let extension = null;
        if (update.newThumbnailFile) {
            thumbnail = await encode(update.newThumbnailFile);
            extension = update.newThumbnailFile.name.split('.').pop();
        }

        const data = await api.EditReportGroup(props.group.reportGroupDefGU ?? props.group.description ?? '', update.description ?? '', thumbnail ?? '', extension ?? '');

        reportBrowser.updateGroup(props.group, {
            description: data.description,
            groupImage: data.groupImage,
        })

        setIsChangingThumbnail(false);
    }

    const onFileChange = (e: Event & { target: HTMLInputElement }) => {
        if (e.target.files) {
            const file = e.target.files[0]
            if (file) {
                setNewThumbnailFile(() => file)
            }
        }
    }

    const onClickTile = (e: MouseEvent) => {
        reportBrowser.setSelectedGroup(props.group)
    }


    return (
        <>
            <div class="report-card-container" style="width: 200px;" onClick={onClickTile}>
                <div class="report-preview-container">
                    <div class="report-preview-image">
                        <img src={props.group.groupImage}></img>
                        <div class="report-preview-image-overlay">
                            <Show when={reportBrowser.state.isEditing}>
                                <div class="report-preview-action-container">
                                    <div class="report-preview-actions-top-row">
                                        <div style="flex-grow: 1">
                                            <Button onClick={onClickChangeThumbnail}>Change Thumbnail</Button>
                                        </div> 
                                    </div>
                                </div>
                            </Show>
                        </div>
                    </div>
                </div>

                <div style="display: flex; align-items: center; width: 100%;">
                    <div class="s-link" style="display: flex; flex-direction: column; width: 100%; align-items: start; padding-top: 8px; font-size: 18px;">
                        <InlineEdit model={groupTitle} disabled={false} onSave={() => save({description: groupTitle[0]()})}></InlineEdit>
                    </div>
                </div>

            </div>
            <STModal show={isChangingThumbnailSignal}>
                <div style="display: flex; align-items: center; margin-bottom: 16px;">
                    <input type="file" onChange={onFileChange}></input>
                    <Button onClick={() => save({ newThumbnailFile: newThumbnailFile() })}>Update Thumbnail</Button>
                </div>
            </STModal>
        </>
    )
}

async function encode(file: File) {
    return new Promise<string>((resolve) => {
        const reader = new FileReader();

        reader.onload = (event: ProgressEvent<FileReader>) => {
            const dataUrl = event.target!.result as string;
            const [_, base64] = dataUrl.split(',');

            resolve(base64);
        };

        reader.readAsDataURL(file);
    });
}


interface ReportCardProps {
    report: ReportInfoCard
}

interface UpdateReportCardRequest {
    name?: string
    number?: string
    editGroupOverride?: string
    newThumbnailFile?: File
}

const ReportTile = (props: ReportCardProps) => {
    const reportBrowser = useReportBrowser()! 

    const nameSignal = createSignal(props.report.name ?? '')
    const numberSignal = createSignal(props.report.number ?? '')

    const expand = () => reportBrowser.expandReport(props.report)
    const save = async (update: UpdateReportCardRequest) => {
        let thumbnail: string = '';
        let extension: string = '';
        if (update.newThumbnailFile) {
            thumbnail = await encode(update.newThumbnailFile);
            extension = update.newThumbnailFile.name.split('.').pop() as string;
        }

        const data = await api.EditReport(props.report.reportGU as string, update?.name ?? '', update?.number ?? '', update?.editGroupOverride ?? '', thumbnail, extension);
        if (data) {
            reportBrowser.updateReport(props.report, data)
        }
    }

    const previewImgStyle = createMemo(() => {
        if (!props.report.reportPreviewImage) return ''
        return props.report.reportPreviewImage.includes('?')
            ? `background-image: url('${props.report.reportPreviewImage}&size=thumbnail');`
            : `background-image: url('${props.report.reportPreviewImage}?size=thumbnail');`
    })

    const [isEditMenuOpen, setIsEditMenuOpen] = createSignal(false)

    return (
        <div class="report-card-container" tabindex="0" data-active={isEditMenuOpen()}>
            <div class="report-preview-container">
                <div class="report-preview-image" style={previewImgStyle()} onClick={expand}>
                    <div class="report-preview-actions-top-row">
                        <button class="report-preview-expand-button" onClick={expand}>
                            <span class="fa fa-expand-alt"></span>
                        </button>
                        <ReportEditMenu report={props.report} isOpen={[isEditMenuOpen, setIsEditMenuOpen]}></ReportEditMenu>
                    </div>
                    <div class="report-preview-actions-main">
                        <Button type="primary" onClick={() => reportBrowser.launchReportInterface(props.report)}>
                            Open Report
                            <span class="fa fa-external-link" style="padding-left: 4px;"></span>
                        </Button>
                    </div>
                </div>
            </div>

            <div style="display: flex; align-items: center; width: 100%;">
                <div style="display: flex; flex-direction: column; width: 100%; align-items: start; padding-top: 8px;">
                    <div class="s-link" style="font-size: 18px;">
                        <InlineEdit model={numberSignal} disabled={!reportBrowser.state.isEditing} onSave={(v) => save({ number: v })}></InlineEdit>
                    </div>
                    <InlineEdit model={nameSignal} onSave={(v) => save({ name: v })}></InlineEdit>
                </div>
            </div>
        </div>
    )
}